/**
 * Phase 22.6 — "asked" and "reviews posted", week by week, for one client.
 *
 * NOT A FUNNEL. These are two independent counts printed side by side. Google never says
 * which request produced which review; reviews arrive from customers nobody asked, and
 * most asks produce nothing. So there is deliberately no rate, ratio, percentage or
 * arrow here — any single number built from both columns would read as cause and effect,
 * and this data cannot carry that. The alert above the table says so on the page, not
 * only in this comment, for the same reason 22.3 leads with "initiated, not delivered".
 *
 * A DASH IS NOT A ZERO. Weeks before the Review Request Tool began logging show "not
 * logged" — requests were being made then, they just were not recorded. Until a first
 * review sync succeeds, the reviews column is "not synced" for every week. Only a week
 * we actually measured shows 0.
 *
 * Visual register matches the rest of the tab (22.3): a log, not a dashboard. No charts,
 * no metric cards, no gradients.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { reputationAPI } from '../../services/api';

const WINDOWS = [12, 26, 52];
const MUTED = '#8b949e';
const FAINT = '#6e7681';

function weekLabel(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** A count we measured, or an explicit reason we cannot show one. */
function Count({ value, unknownLabel, unknownTitle }) {
  if (value == null) {
    return (
      <Tooltip title={unknownTitle}>
        <Typography component="span" variant="body2" sx={{ color: FAINT, fontStyle: 'italic' }}>
          {unknownLabel}
        </Typography>
      </Tooltip>
    );
  }
  return (
    <Typography component="span" variant="body2" sx={{ color: value ? '#f0f6fc' : MUTED }}>
      {value}
    </Typography>
  );
}

export default function ReviewCorrelation({ clientId, clientName }) {
  const [weeks, setWeeks] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (clientId == null) return;
    setLoading(true);
    setError(null);
    try {
      setData(await reputationAPI.getReviewCorrelation({ clientId, weeks }));
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Could not load the comparison');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [clientId, weeks]);

  useEffect(() => {
    load();
  }, [load]);

  const gbp = data?.gbp;
  const rows = data?.weeks || [];
  const totalAsked = rows.reduce((n, w) => n + (w.requests_initiated || 0), 0);
  const totalReviews = rows.reduce((n, w) => n + (w.reviews_posted || 0), 0);
  const anySynced = rows.some((w) => w.reviews_posted != null);

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', mb: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(139, 148, 158, 0.2)' }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ color: MUTED }}>
            Requests and reviews, week by week
          </Typography>
          {gbp?.location_title ? (
            <Typography variant="caption" sx={{ color: FAINT }}>
              Google listing: {gbp.location_title}
            </Typography>
          ) : null}
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={weeks}
          onChange={(_e, v) => v && setWeeks(v)}
          aria-label="How many weeks to show"
        >
          {WINDOWS.map((w) => (
            <ToggleButton key={w} value={w} sx={{ textTransform: 'none' }}>
              {w} weeks
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      {/* The thing this table must never be read as. */}
      <Box sx={{ px: 2, pt: 2 }}>
        <Alert severity="info">
          These are two separate counts over the same weeks, <strong>not</strong> a funnel.
          Google does not say which request led to which review, so nothing here attributes
          a review to a request — reviews also come from customers who were never asked.
          Read it for rough timing, not cause.
        </Alert>
      </Box>

      {error ? (
        <Box sx={{ px: 2, pt: 2 }}>
          <Alert severity="error" action={<Button size="small" onClick={load}>Retry</Button>}>
            {error}
          </Alert>
        </Box>
      ) : null}

      {!error && gbp && !gbp.connected ? (
        <Box sx={{ px: 2, pt: 2 }}>
          <Alert severity="warning">
            No Google listing is connected for {clientName || 'this client'}, so the reviews
            column is unknown. Connect one on the{' '}
            <Link href={`/clients?client_id=${clientId}`} underline="hover">
              Clients page
            </Link>
            . Requests are still logged in the meantime.
          </Alert>
        </Box>
      ) : null}

      {!error && gbp?.connected && !anySynced ? (
        <Box sx={{ px: 2, pt: 2 }}>
          <Alert severity="warning">
            The listing is connected but no review sync has finished yet, so the reviews
            column is unknown rather than zero. The nightly sync runs at 02:50 UTC.
          </Alert>
        </Box>
      ) : null}

      {!error && gbp?.last_error ? (
        <Box sx={{ px: 2, pt: 2 }}>
          <Alert severity="warning">
            The last review sync failed: {String(gbp.last_error).slice(0, 300)}. Counts are
            as of the last successful sync, not zero.
          </Alert>
        </Box>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Week of</TableCell>
                <TableCell align="right">
                  {/* "Logged", not "asked": the tool logs each hand-off path separately,
                      so one customer asked once via both Open Messages and Copy text
                      appears twice. Verified against live rows, 2026-09-17. */}
                  <Tooltip title="Hand-offs logged by the Review Request Tool. Opening Messages and copying the text are logged separately, so asking one customer both ways counts twice. Initiated, never confirmed as delivered.">
                    <span>Requests logged</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Reviews Google records as written that week for the connected listing.">
                    <span>Reviews posted</span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((w) => (
                <TableRow key={w.week_start} hover>
                  <TableCell>
                    {weekLabel(w.week_start)}
                    {w.in_progress ? (
                      <Typography component="span" variant="caption" sx={{ color: FAINT, ml: 1 }}>
                        this week so far
                      </Typography>
                    ) : null}
                    {w.send_log_coverage === 'partial' ? (
                      <Typography component="span" variant="caption" sx={{ color: FAINT, ml: 1 }}>
                        logging began mid-week
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">
                    <Count
                      value={w.requests_initiated}
                      unknownLabel="not logged"
                      unknownTitle="The Review Request Tool was not logging sends yet this week. Requests may well have been made."
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Count
                      value={w.reviews_posted}
                      unknownLabel="not synced"
                      unknownTitle="No completed review sync covers this week yet."
                    />
                    {w.reviews_since_removed ? (
                      <Tooltip title="Counted in that week, but Google no longer shows it.">
                        <Typography component="span" variant="caption" sx={{ color: FAINT, ml: 1 }}>
                          ({w.reviews_since_removed} since removed)
                        </Typography>
                      </Tooltip>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && rows.length > 0 ? (
        <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid rgba(139, 148, 158, 0.2)' }}>
          <Typography variant="caption" sx={{ color: MUTED }}>
            Over these {weeks} weeks: {totalAsked} request{totalAsked === 1 ? '' : 's'}{' '}
            logged, {anySynced ? `${totalReviews} review${totalReviews === 1 ? '' : 's'} posted` : 'reviews not synced'}
            . Weeks are Monday to Sunday, UTC.
            {gbp?.google_reported_total != null
              ? ` Google reports ${gbp.google_reported_total} reviews on this listing in total, including older ones.`
              : ''}
          </Typography>
        </Box>
      ) : null}
    </Paper>
  );
}
